import {
  Work,
  CreateWorkDto,
  UpdateWorkDto,
  ProgressUpdateData,
  WorkFilters,
  WorkListResponse,
  BulkOperationResult,
  DeleteWorkResponse,
  DashboardData,
  ProgressTracking,
  PerformanceAnalytics,
  DistrictSummary,
  StageSummary,
  ExcelExportResult,
  ImportValidationResult,
  QuickStats,
  FieldOptions,
  ValidationRules,
  HealthCheckResponse,
  SearchCriteria,
  BatchOperation,
  ApiResponse,
  WorkStage,
  GrantLevel,
  WorkType,
  CategoryPST,
  WorkSummary
} from '../types/work.types';
import * as XLSX from 'xlsx';

// ==========================================
// FULL 38-COLUMN "WW FORMAT" ROW
// ==========================================
// One flattened row from GET /works/full-format. Hierarchy names are
// read-only; the FY-snapshot fields (fy_*) are the raw inputs the grid's
// formula columns (balance / cumulative) are derived from.
export interface FullFormatRow {
  work_id: number;
  sno: number | null;
  work_type_id: number | null;
  oas_grant_level: string | null;
  grant_type: string | null;
  territory_name: string | null;
  circle_name: string | null;
  district_name: string | null;
  division_name: string | null;
  sub_division_name: string | null;
  mandal_name: string | null;
  constituency_name: string | null;
  work_name: string | null;
  oas_proceedings_number: string | null;
  oas_sanction_date: string | null;
  ras_proceedings_number: string | null;
  ras_sanction_date: string | null;
  work_type: string | null;
  work_sub_type: string | null;
  length_sanctioned_km: number | null;
  estimate_cost: number | null;
  agency_name: string | null;
  tp: string | null;
  agreement_date: string | null;
  completion_date_set: string | null;
  ras_amount: number | null;
  abstract_stage_of_work: string | null;
  sub_stage_of_work: string | null;
  fy_length_upto_last_fy: number | null;
  fy_length_during: number | null;
  current_cumulative_length_km: number | null;
  fy_exp_upto_last_fy: number | null;
  fy_exp_during: number | null;
  current_cumulative_expenditure: number | null;
  bills_pending_pao_bro_loc: number | null;
  work_done_not_recorded_bills: number | null;
  category_pst: string | null;
  detailed_stage_of_work: string | null;
  // Read-only monitoring columns (mirrors the dashboard EOAT / QC tiles).
  is_eoat: number | null;
  eoat_days: number | null;
  qc_status: string | null;
  qc_wing: string | null;
  qc_grade: string | null;
  progress_id: number | null;
  financial_year: string | null;
}

// ==========================================
// API CONFIGURATION
// ==========================================

const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1',
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000
};

// ==========================================
// HTTP CLIENT
// ==========================================

class HttpClient {
  private baseUrl: string;
  private timeout: number;

  constructor(baseUrl: string, timeout: number = 30000) {
    this.baseUrl = baseUrl;
    this.timeout = timeout;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs?: number
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const controller = new AbortController();

    // Set timeout (callers may override for slow operations such as bulk import)
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs ?? this.timeout);

    try {
      // Attach JWT (stored at login under localStorage 'token'). Skip
      // Content-Type for FormData so the browser sets the multipart boundary.
      const token =
        typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
      const isFormData =
        typeof FormData !== 'undefined' && options.body instanceof FormData;
      const baseHeaders: Record<string, string> = {};
      if (!isFormData) baseHeaders['Content-Type'] = 'application/json';
      if (token) baseHeaders['Authorization'] = `Bearer ${token}`;

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          ...baseHeaders,
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    const url = params ? `${endpoint}?${this.buildQueryString(params)}` : endpoint;
    return this.request<T>(url);
  }

  async post<T>(endpoint: string, data?: any, timeoutMs?: number): Promise<T> {
    const isForm = typeof FormData !== 'undefined' && data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data === undefined ? undefined : isForm ? data : JSON.stringify(data),
    }, timeoutMs);
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async upload<T>(endpoint: string, file: File, additionalData?: Record<string, any>): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value.toString());
      });
    }

    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for multipart/form-data
    });
  }

  public buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v.toString()));
        } else if (value instanceof Date) {
          searchParams.append(key, value.toISOString().split('T')[0]);
        } else {
          searchParams.append(key, value.toString());
        }
      }
    });
    
    return searchParams.toString();
  }
}

// ==========================================
// WORKS API SERVICE
// ==========================================

export class WorksApiService {
  private http: HttpClient;
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  private readonly endpoints = {
    works: '/works',
    bulkWorks: '/works/bulk',
    workById: (id: number) => `/works/${id}`,
    workBySno: (sno: number) => `/works/sno/${sno}`,
    worksByDistrict: (districtId: number) => `/works/district/${districtId}`,
    worksByDivision: (divisionId: number) => `/works/division/${divisionId}`, // New endpoint
    worksBySubDivision: (subDivisionId: number) => `/works/subdivision/${subDivisionId}`, // New endpoint
    worksByMandal: (mandalId: number) => `/works/mandal/${mandalId}`,
    worksSummaryByDivision: (divisionId: number) => `/works/division/${divisionId}/summary`, // New summary endpoint
    worksSummaryBySubDivision: (subDivisionId: number) => `/works/subdivision/${subDivisionId}/summary`, // New summary endpoint
    updateProgress: (id: number) => `/works/${id}/progress`,
    bulkUpdate: '/works/bulk-update',
    restore: (id: number) => `/works/${id}/restore`,
    dashboardFinancial: '/works/dashboard/financial',
    progressTracking: '/works/dashboard/progress-tracking',
    performanceAnalytics: '/works/analytics/performance',
    completionForecast: '/works/analytics/completion-forecast',
    districtSummary: '/works/summary/district-wise',
    stageSummary: '/works/summary/stage-wise',
    pendingBills: '/works/reports/pending-bills',
    exportExcel: '/works/export/excel',
    importValidate: '/works/import/excel/validate',
    importExcel: '/works/import/excel',
    advancedSearch: '/works/search/advanced',
    batchStageUpdate: '/works/batch/stage-update',
    batchContractorAssign: '/works/batch/contractor-assign',
    batchUpdateByDivision: '/works/batch/division', // New batch endpoint
    batchUpdateBySubDivision: '/works/batch/subdivision', // New batch endpoint
    validateDivisionSubDivision: '/works/validate/division-subdivision', // New validation endpoint
    fieldOptions: '/works/utility/field-options',
    validationRules: '/works/utility/validation-rules',
    quickStats: '/works/stats/quick',
    healthCheck: '/works/health/check'
  };

  constructor(baseUrl: string = API_CONFIG.baseURL) {
    this.http = new HttpClient(baseUrl, API_CONFIG.timeout);
  }

  // ==========================================
  // CRUD OPERATIONS
  // ==========================================

  async getAllWorks(filters: WorkFilters = {}): Promise<WorkListResponse> {
    try {
      return await this.http.get<WorkListResponse>(this.endpoints.works, filters);
    } catch (error) {
      throw new Error(`Failed to fetch works: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getWorkById(id: number): Promise<Work> {
    try {
      return await this.http.get<Work>(this.endpoints.workById(id));
    } catch (error) {
      throw new Error(`Failed to fetch work with ID ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Full 38-column "WW Format" grid: list flattened rows, and save an edited row.
  async getWorksFullFormat(params?: Record<string, any>): Promise<FullFormatRow[]> {
    try {
      return await this.http.get<FullFormatRow[]>('/works/full-format', params);
    } catch (error) {
      throw new Error(`Failed to fetch full-format works: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Full-Format Sub-Stage dropdown options from the work_stages master (single
  // source of truth): gate stages bucketed by abstract stage + detailed execution
  // stages by work_type_id. On failure the grid falls back to built-in constants.
  async getFullFormatStageOptions(): Promise<{ gates: Record<string, string[]>; detailedByType: Record<string, string[]> }> {
    try {
      return await this.http.get<{ gates: Record<string, string[]>; detailedByType: Record<string, string[]> }>('/work-stages/full-format-options');
    } catch {
      return { gates: {}, detailedByType: {} };
    }
  }

  // Create a new work from a full-format row (needs district_id + mandal_id).
  async createWorkFullFormat(row: Record<string, any>, userId?: number): Promise<{ success: boolean; work_id: number }> {
    try {
      const qs = userId ? `?${this.http.buildQueryString({ userId })}` : '';
      return await this.http.post<{ success: boolean; work_id: number }>(`/works/full-format${qs}`, row);
    } catch (error) {
      throw new Error(`Failed to create work: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Dry run: validate an Excel file WITHOUT writing anything. Returns the
  // per-row breakdown (valid / invalid + errors) and a summary so the UI can
  // preview the import before committing.
  async validateWorksExcel(file: File): Promise<ImportValidationResult & { message?: string }> {
    const form = new FormData();
    form.append('file', file);
    try {
      return await this.http.post<ImportValidationResult & { message?: string }>(
        '/works-excel-import/validate',
        form,
      );
    } catch (error) {
      throw new Error(`Validation failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Commit: import the valid rows into the database. Pass confirm=true so the
  // backend imports the valid rows and skips any invalid ones (the caller is
  // expected to have shown the dry-run preview first). Uses a longer timeout
  // because a large import creates rows one by one.
  async commitWorksExcel(file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    try {
      return await this.http.post<any>('/works-excel-import/import?confirm=true', form, 180000);
    } catch (error) {
      throw new Error(`Import failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async saveWorkFullFormat(id: number, row: Partial<FullFormatRow>, userId?: number): Promise<{ success: boolean; work_id: number }> {
    try {
      const qs = userId ? `?${this.http.buildQueryString({ userId })}` : '';
      return await this.http.put<{ success: boolean; work_id: number }>(`/works/${id}/full-format${qs}`, row);
    } catch (error) {
      throw new Error(`Failed to save work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Progress ledger: record a period entry, and read a work's progress history
  async recordProgress(id: number, data: any, userId?: number): Promise<any> {
    try {
      const params = userId ? { userId } : {};
      const qs = Object.keys(params).length ? `?${this.http.buildQueryString(params)}` : '';
      return await this.http.post<any>(`${this.endpoints.updateProgress(id)}${qs}`, data);
    } catch (error) {
      throw new Error(`Failed to record progress for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getProgressHistory(id: number): Promise<any[]> {
    try {
      return await this.http.get<any[]>(this.endpoints.updateProgress(id));
    } catch (error) {
      throw new Error(`Failed to fetch progress history for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Verify the latest recorded progress (senior officers only; backend enforces role + scope).
  async verifyProgress(id: number): Promise<any> {
    try {
      return await this.http.post<any>(`/works/${id}/progress/verify`, {});
    } catch (error) {
      throw new Error(`${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Unified activity timeline (changes, stage, progress, verify, sanctions, funds, inspections).
  async getWorkTimeline(id: number): Promise<{ work_id: number; count: number; events: any[] }> {
    try {
      return await this.http.get<any>(`/works/${id}/timeline`);
    } catch (error) {
      throw new Error(`Failed to fetch timeline for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Fund track (allotment → SNA/PFMS → RA-bill → payment)
  async recordFund(id: number, data: any): Promise<any> {
    try {
      return await this.http.post<any>(`/works/${id}/funds`, data);
    } catch (error) {
      throw new Error(`Failed to record fund for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  async getFundHistory(id: number): Promise<any[]> {
    try {
      return await this.http.get<any[]>(`/works/${id}/funds`);
    } catch (error) {
      throw new Error(`Failed to fetch fund history for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  async getFundSummary(id: number): Promise<any> {
    try {
      return await this.http.get<any>(`/works/${id}/funds/summary`);
    } catch (error) {
      throw new Error(`Failed to fetch fund summary for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // DLP / closure
  async recordClosure(id: number, data: any): Promise<any> {
    try {
      return await this.http.post<any>(`/works/${id}/closure`, data);
    } catch (error) {
      throw new Error(`Failed to record closure for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  async getClosure(id: number): Promise<any> {
    try {
      return await this.http.get<any>(`/works/${id}/closure`);
    } catch (error) {
      throw new Error(`Failed to fetch closure for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // QC inspections (V&QC / PR) — drives the QC Reports tile
  async recordInspection(id: number, data: any): Promise<any> {
    try {
      return await this.http.post<any>(`/works/${id}/inspections`, data);
    } catch (error) {
      throw new Error(`Failed to record inspection for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  async getInspectionHistory(id: number): Promise<any[]> {
    try {
      return await this.http.get<any[]>(`/works/${id}/inspections`);
    } catch (error) {
      throw new Error(`Failed to fetch inspections for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Bank Guarantee — drives the BGs-revalidations tile
  async recordBankGuarantee(id: number, data: any): Promise<any> {
    try {
      return await this.http.post<any>(`/works/${id}/bank-guarantee`, data);
    } catch (error) {
      throw new Error(`Failed to record bank guarantee for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Sanctions (OAS / RAS / TS)
  async recordSanction(id: number, data: any): Promise<any> {
    try {
      return await this.http.post<any>(`/works/${id}/sanctions`, data);
    } catch (error) {
      throw new Error(`Failed to record sanction for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  async getSanctions(id: number): Promise<any[]> {
    try {
      return await this.http.get<any[]>(`/works/${id}/sanctions`);
    } catch (error) {
      throw new Error(`Failed to fetch sanctions for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Advance Stage — one-click forward step along the lifecycle pipeline
  async getNextStage(id: number): Promise<any> {
    try {
      return await this.http.get<any>(`/works/${id}/next-stage`);
    } catch (error) {
      throw new Error(`Failed to fetch next stage for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  async advanceStage(id: number): Promise<any> {
    try {
      return await this.http.post<any>(`/works/${id}/advance-stage`, {});
    } catch (error) {
      throw new Error(`Failed to advance stage for work ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Delegation of powers: competent authority for an action + cost (lakhs)
  async resolveAuthority(action: 'AS' | 'TS' | 'TENDER_ACCEPTANCE', cost: number, scheme?: string): Promise<any> {
    try {
      return await this.http.get<any>('/authority-slabs/resolve', { action, cost, scheme: scheme || 'ALL' });
    } catch (error) {
      return { action, cost, authority: null };
    }
  }

  async getWorkBySno(sno: number): Promise<Work> {
    try {
      return await this.http.get<Work>(this.endpoints.workBySno(sno));
    } catch (error) {
      throw new Error(`Failed to fetch work with SNO ${sno}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getWorksByDistrict(districtId: number, stage?: WorkStage): Promise<Work[]> {
    try {
      const params = stage ? { stage } : {};
      return await this.http.get<Work[]>(this.endpoints.worksByDistrict(districtId), params);
    } catch (error) {
      throw new Error(`Failed to fetch works for district ${districtId}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async createWork(work: CreateWorkDto, userId?: number): Promise<Work> {
    try {
      const params = userId ? { userId } : {};
      return await this.http.post<Work>(`${this.endpoints.works}?${this.http.buildQueryString(params)}`, work);
    } catch (error) {
      throw new Error(`Failed to create work: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async bulkCreateWorks(works: CreateWorkDto[], userId?: number): Promise<BulkOperationResult> {
    try {
      const params = userId ? { userId } : {};
      return await this.http.post<BulkOperationResult>(`${this.endpoints.bulkWorks}?${this.http.buildQueryString(params)}`, works);
    } catch (error) {
      throw new Error(`Failed to bulk create works: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async updateWork(id: number, work: UpdateWorkDto, userId?: number): Promise<Work> {
    try {
      const params = userId ? { userId } : {};
      return await this.http.put<Work>(`${this.endpoints.workById(id)}?${this.http.buildQueryString(params)}`, work);
    } catch (error) {
      throw new Error(`Failed to update work with ID ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async updateWorkProgress(id: number, progress: ProgressUpdateData, userId?: number): Promise<Work> {
    try {
      const params = userId ? { userId } : {};
      return await this.http.put<Work>(`${this.endpoints.updateProgress(id)}?${this.http.buildQueryString(params)}`, progress);
    } catch (error) {
      throw new Error(`Failed to update work progress for ID ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async bulkUpdateWorks(updates: Array<{ work_id: number; data: UpdateWorkDto }>, userId?: number): Promise<BulkOperationResult> {
    try {
      const params = userId ? { userId } : {};
      return await this.http.put<BulkOperationResult>(`${this.endpoints.bulkUpdate}?${this.http.buildQueryString(params)}`, updates);
    } catch (error) {
      throw new Error(`Failed to bulk update works: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async deleteWork(id: number, userId?: number): Promise<DeleteWorkResponse> {
    try {
      const params = userId ? { userId } : {};
      return await this.http.delete<DeleteWorkResponse>(`${this.endpoints.workById(id)}?${this.http.buildQueryString(params)}`);
    } catch (error) {
      throw new Error(`Failed to delete work with ID ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async restoreWork(id: number, userId?: number): Promise<Work> {
    try {
      const params = userId ? { userId } : {};
      return await this.http.put<Work>(`${this.endpoints.restore(id)}?${this.http.buildQueryString(params)}`);
    } catch (error) {
      throw new Error(`Failed to restore work with ID ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // ==========================================
  // DASHBOARD & ANALYTICS
  // ==========================================

  async getFinancialDashboard(): Promise<DashboardData> {
    try {
      return await this.getCached('dashboard-financial', 
        () => this.http.get<DashboardData>(this.endpoints.dashboardFinancial)
      );
    } catch (error) {
      throw new Error(`Failed to fetch financial dashboard: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getProgressTracking(): Promise<ProgressTracking> {
    try {
      return await this.getCached('progress-tracking',
        () => this.http.get<ProgressTracking>(this.endpoints.progressTracking)
      );
    } catch (error) {
      throw new Error(`Failed to fetch progress tracking: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getPerformanceAnalytics(): Promise<PerformanceAnalytics> {
    try {
      return await this.getCached('performance-analytics',
        () => this.http.get<PerformanceAnalytics>(this.endpoints.performanceAnalytics)
      );
    } catch (error) {
      throw new Error(`Failed to fetch performance analytics: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getCompletionForecast(): Promise<any> {
    try {
      return await this.http.get(this.endpoints.completionForecast);
    } catch (error) {
      throw new Error(`Failed to fetch completion forecast: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getDistrictWiseSummary(): Promise<DistrictSummary[]> {
    try {
      return await this.getCached('district-summary',
        () => this.http.get<DistrictSummary[]>(this.endpoints.districtSummary)
      );
    } catch (error) {
      throw new Error(`Failed to fetch district summary: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getStageWiseSummary(): Promise<StageSummary[]> {
    try {
      return await this.getCached('stage-summary',
        () => this.http.get<StageSummary[]>(this.endpoints.stageSummary)
      );
    } catch (error) {
      throw new Error(`Failed to fetch stage summary: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getPendingBillsReport(): Promise<Work[]> {
    try {
      return await this.http.get<Work[]>(this.endpoints.pendingBills);
    } catch (error) {
      throw new Error(`Failed to fetch pending bills report: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getQuickStats(): Promise<QuickStats> {
    try {
      return await this.getCached('quick-stats',
        () => this.http.get<QuickStats>(this.endpoints.quickStats),
        60000 // 1 minute cache
      );
    } catch (error) {
      throw new Error(`Failed to fetch quick stats: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // ==========================================
  // ADVANCED SEARCH
  // ==========================================

  async advancedSearch(
    criteria: SearchCriteria & { page?: number; limit?: number }
  ): Promise<WorkListResponse> {
    try {
      return await this.http.get<WorkListResponse>(this.endpoints.advancedSearch, criteria);
    } catch (error) {
      throw new Error(`Failed to perform advanced search: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // ==========================================
  // IMPORT/EXPORT OPERATIONS
  // ==========================================

  async exportToExcel(filters: WorkFilters & { divisionId?: number; subDivisionId?: number } = {}): Promise<ExcelExportResult> {
    try {
      return await this.http.get<ExcelExportResult>(this.endpoints.exportExcel, filters);
    } catch (error) {
      throw new WorksApiError(
        `Failed to export to Excel: ${error instanceof Error ? error.message : String(error)}`,
        'EXPORT_EXCEL_ERROR'
      );
    }
  }

  async validateExcelImport(file: File, userId?: number): Promise<ImportValidationResult> {
    try {
      const additionalData = userId ? { userId } : {};
      return await this.http.upload<ImportValidationResult>(this.endpoints.importValidate, file, additionalData);
    } catch (error) {
      throw new Error(`Failed to validate Excel import: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async importFromExcel(file: File, userId?: number): Promise<ImportValidationResult> {
    try {
      const additionalData = userId ? { userId } : {};
      return await this.http.upload<ImportValidationResult>(this.endpoints.importExcel, file, additionalData);
    } catch (error) {
      throw new Error(`Failed to import from Excel: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // ==========================================
  // BATCH OPERATIONS
  // ==========================================

  async batchUpdateStages(
    workIds: number[], 
    newStage: WorkStage, 
    subStage?: string, 
    userId?: number
  ): Promise<BulkOperationResult> {
    try {
      const data = { workIds, newStage, subStage };
      const params = userId ? { userId } : {};
      return await this.http.post<BulkOperationResult>(`${this.endpoints.batchStageUpdate}?${this.http.buildQueryString(params)}`, data);
    } catch (error) {
      throw new Error(`Failed to batch update stages: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async batchAssignContractor(
    workIds: number[], 
    contractorId: number, 
    userId?: number
  ): Promise<BulkOperationResult> {
    try {
      const data = { workIds, contractorId };
      const params = userId ? { userId } : {};
      return await this.http.post<BulkOperationResult>(`${this.endpoints.batchContractorAssign}?${this.http.buildQueryString(params)}`, data);
    } catch (error) {
      throw new Error(`Failed to batch assign contractor: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async executeBatchOperation(operation: BatchOperation, userId?: number): Promise<BulkOperationResult> {
    switch (operation.operation) {
      case 'stage_update':
        return this.batchUpdateStages(
          operation.workIds, 
          operation.data?.newStage!, 
          operation.data?.subStage, 
          userId
        );
      case 'contractor_assign':
        return this.batchAssignContractor(
          operation.workIds, 
          operation.data?.contractorId!, 
          userId
        );
      default:
        throw new Error(`Unsupported batch operation: ${operation.operation}`);
    }
  }

  // ==========================================
  // UTILITY ENDPOINTS
  // ==========================================

  async getFieldOptions(): Promise<FieldOptions> {
    try {
      return await this.getCached('field-options',
        () => this.http.get<FieldOptions>(this.endpoints.fieldOptions),
        300000 // 5 minutes cache for static data
      );
    } catch (error) {
      throw new Error(`Failed to fetch field options: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getValidationRules(): Promise<ValidationRules> {
    try {
      return await this.getCached('validation-rules',
        () => this.http.get<ValidationRules>(this.endpoints.validationRules),
        300000 // 5 minutes cache for static data
      );
    } catch (error) {
      throw new Error(`Failed to fetch validation rules: ${error instanceof Error ? error.message : String(error)}`);
    }
  }


  /////////////


  async getWorksByDivision(
    divisionId: number,
    filters: {
      stage?: WorkStage;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
    } = {}
  ): Promise<{
    summary: WorkSummary;
    works: Work[];
    total: number;
    totalPages: number;
    currentPage: number;
  }> {
    try {
      const cacheKey = `works-division-${divisionId}-${JSON.stringify(filters)}`;
      return await this.withRetry(() =>
        this.getCached(cacheKey, () =>
          this.http.get<{
            summary: WorkSummary;
            works: Work[];
            total: number;
            totalPages: number;
            currentPage: number;
          }>(this.endpoints.worksByDivision(divisionId), filters)
        )
      );
    } catch (error) {
      throw new WorksApiError(
        `Failed to fetch works for division ${divisionId}: ${error instanceof Error ? error.message : String(error)}`,
        'FETCH_WORKS_BY_DIVISION_ERROR'
      );
    }
  }
  
  async getWorksBySubDivision(
    subDivisionId: number,
    filters: {
      stage?: WorkStage;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
    } = {}
  ): Promise<{
    summary: WorkSummary;
    works: Work[];
    total: number;
    totalPages: number;
    currentPage: number;
  }> {
    try {
      const cacheKey = `works-subdivision-${subDivisionId}-${JSON.stringify(filters)}`;
      return await this.withRetry(() =>
        this.getCached(cacheKey, () =>
          this.http.get<{
            summary: WorkSummary;
            works: Work[];
            total: number;
            totalPages: number;
            currentPage: number;
          }>(this.endpoints.worksBySubDivision(subDivisionId), filters)
        )
      );
    } catch (error) {
      throw new WorksApiError(
        `Failed to fetch works for sub-division ${subDivisionId}: ${error instanceof Error ? error.message : String(error)}`,
        'FETCH_WORKS_BY_SUBDIVISION_ERROR'
      );
    }
  }
  
  async getWorksSummaryByDivision(divisionId: number, stage?: WorkStage): Promise<WorkSummary> {
    try {
      const cacheKey = `works-summary-division-${divisionId}-${stage || 'all'}`;
      return await this.withRetry(() =>
        this.getCached(cacheKey, () =>
          this.http.get<WorkSummary>(this.endpoints.worksSummaryByDivision(divisionId), { stage })
        )
      );
    } catch (error) {
      throw new WorksApiError(
        `Failed to fetch work summary for division ${divisionId}: ${error instanceof Error ? error.message : String(error)}`,
        'FETCH_WORKS_SUMMARY_BY_DIVISION_ERROR'
      );
    }
  }
  
  async getWorksSummaryBySubDivision(subDivisionId: number, stage?: WorkStage): Promise<WorkSummary> {
    try {
      const cacheKey = `works-summary-subdivision-${subDivisionId}-${stage || 'all'}`;
      return await this.withRetry(() =>
        this.getCached(cacheKey, () =>
          this.http.get<WorkSummary>(this.endpoints.worksSummaryBySubDivision(subDivisionId), { stage })
        )
      );
    } catch (error) {
      throw new WorksApiError(
        `Failed to fetch work summary for sub-division ${subDivisionId}: ${error instanceof Error ? error.message : String(error)}`,
        'FETCH_WORKS_SUMMARY_BY_SUBDIVISION_ERROR'
      );
    }
  }
  
  async batchUpdateWorksByDivision(
    divisionId: number,
    updates: Array<{ work_id: number; data: UpdateWorkDto }>,
    userId?: number
  ): Promise<BulkOperationResult> {
    try {
      const params = userId ? { userId } : {};
      const data = { divisionId, updates };
      const result = await this.withRetry(() =>
        this.http.post<BulkOperationResult>(
          `${this.endpoints.batchUpdateByDivision}?${this.http.buildQueryString(params)}`,
          data
        )
      );
      this.clearCache('works-division');
      this.clearCache('works-summary-division');
      return result;
    } catch (error) {
      throw new WorksApiError(
        `Failed to batch update works for division ${divisionId}: ${error instanceof Error ? error.message : String(error)}`,
        'BATCH_UPDATE_WORKS_BY_DIVISION_ERROR'
      );
    }
  }
  
  async batchUpdateWorksBySubDivision(
    subDivisionId: number,
    updates: Array<{ work_id: number; data: UpdateWorkDto }>,
    userId?: number
  ): Promise<BulkOperationResult> {
    try {
      const params = userId ? { userId } : {};
      const data = { subDivisionId, updates };
      const result = await this.withRetry(() =>
        this.http.post<BulkOperationResult>(
          `${this.endpoints.batchUpdateBySubDivision}?${this.http.buildQueryString(params)}`,
          data
        )
      );
      this.clearCache('works-subdivision');
      this.clearCache('works-summary-subdivision');
      return result;
    } catch (error) {
      throw new WorksApiError(
        `Failed to batch update works for sub-division ${subDivisionId}: ${error instanceof Error ? error.message : String(error)}`,
        'BATCH_UPDATE_WORKS_BY_SUBDIVISION_ERROR'
      );
    }
  }
  
  async validateDivisionSubDivisionRelationship(
    divisionId: number,
    subDivisionId: number
  ): Promise<{ valid: boolean; message?: string }> {
    try {
      return await this.withRetry(() =>
        this.http.get<{ valid: boolean; message?: string }>(
          this.endpoints.validateDivisionSubDivision,
          { divisionId, subDivisionId }
        )
      );
    } catch (error) {
      throw new WorksApiError(
        `Failed to validate division-subdivision relationship: ${error instanceof Error ? error.message : String(error)}`,
        'VALIDATE_DIVISION_SUBDIVISION_ERROR'
      );
    }
  }
  //////////////

  async healthCheck(): Promise<HealthCheckResponse> {
    try {
      return await this.http.get<HealthCheckResponse>(this.endpoints.healthCheck);
    } catch (error) {
      throw new Error(`Health check failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // ==========================================
  // SPECIALIZED QUERIES
  // ==========================================

  async getWorksByFilters(filters: WorkFilters): Promise<Work[]> {
    try {
      const response = await this.getAllWorks(filters);
      return response.works;
    } catch (error) {
      throw new Error(`Failed to fetch works by filters: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getWorksByStage(stage: WorkStage, limit?: number): Promise<Work[]> {
    try {
      const response = await this.getAllWorks({ stage, limit: limit || 100 });
      return response.works;
    } catch (error) {
      throw new Error(`Failed to fetch works by stage: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getDelayedWorks(): Promise<Work[]> {
    try {
      const today = new Date();
      const response = await this.getAllWorks({
        toDate: today,
        stage: WorkStage.IN_PROGRESS
      });
      
      return response.works.filter(work => 
        work.completion_date_set && 
        new Date(work.completion_date_set) < today
      );
    } catch (error) {
      throw new Error(`Failed to fetch delayed works: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getHighValueWorks(minAmount: number = 1000000): Promise<Work[]> {
    try {
      const response = await this.getAllWorks({ minCost: minAmount });
      return response.works;
    } catch (error) {
      throw new Error(`Failed to fetch high value works: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getRecentWorks(days: number = 30): Promise<Work[]> {
    try {
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - days);
      
      const response = await this.getAllWorks({ fromDate });
      return response.works;
    } catch (error) {
      throw new Error(`Failed to fetch recent works: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // ==========================================
  // REPORTING OPERATIONS
  // ==========================================

  async generateProgressReport(districtId?: number): Promise<{
    summary: WorkSummary;
    districtProgress: DistrictSummary[];
    stageProgress: StageSummary[];
    delayedWorks: Array<{
      work_id: number;
      sno: number;
      work_name: string;
      agency_name: string;
      completion_date_set: string;
      abstract_stage_of_work: WorkStage;
      estimate_cost: number;
      cum_exp_on_work: number;
      district_name: string;
      mandal_name: string;
    }>;
    completionForecast: any;
  }> {
    try {
      const filters = districtId ? { districtId } : {};
      
      const [worksResponse, districtProgress, stageProgress, progressTracking] = await Promise.all([
        this.getAllWorks(filters),
        this.getDistrictWiseSummary(),
        this.getStageWiseSummary(),
        this.getProgressTracking()
      ]);

      return {
        summary: worksResponse.summary,
        districtProgress: districtId ? districtProgress.filter(d => d.district_id === districtId) : districtProgress,
        stageProgress,
        delayedWorks: progressTracking.delayedWorks,
        completionForecast: progressTracking.completionForecast
      };
    } catch (error) {
      throw new Error(`Failed to generate progress report: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async generateFinancialReport(): Promise<{
    overview: DashboardData['overview'];
    grantAnalysis: DashboardData['grantTypeAnalysis'];
    pendingBills: Work[];
    expenditureTrends: DashboardData['trendAnalysis'];
  }> {
    try {
      const [dashboard, pendingBills] = await Promise.all([
        this.getFinancialDashboard(),
        this.getPendingBillsReport()
      ]);

      return {
        overview: dashboard.overview,
        grantAnalysis: dashboard.grantTypeAnalysis,
        pendingBills,
        expenditureTrends: dashboard.trendAnalysis
      };
    } catch (error) {
      throw new Error(`Failed to generate financial report: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // ==========================================
  // CACHING LAYER
  // ==========================================

  async getCached<T>(key: string, fetcher: () => Promise<T>, ttl: number = this.DEFAULT_TTL): Promise<T> {
    const cached = this.cache.get(key);
    
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data;
    }
    
    const data = await fetcher();
    this.cache.set(key, { data, timestamp: Date.now(), ttl });
    
    return data;
  }

  clearCache(pattern?: string): void {
    if (pattern) {
      const keysToDelete: string[] = [];
      this.cache.forEach((_, key) => {
        if (key.includes(pattern)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.cache.delete(key));
    } else {
      this.cache.clear();
    }
  }

  // ==========================================
  // ERROR HANDLING & RETRY LOGIC
  // ==========================================

  async withRetry<T>(
    operation: () => Promise<T>, 
    maxAttempts: number = API_CONFIG.retryAttempts
  ): Promise<T> {
    let lastError: Error | unknown;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        
        if (attempt === maxAttempts) {
          break;
        }
        
        // Only retry on network errors, not validation errors
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('Failed to fetch') || 
            errorMessage.includes('timeout') ||
            errorMessage.includes('network')) {
          await this.delay(API_CONFIG.retryDelay * attempt);
          continue;
        } else {
          throw error; // Don't retry validation or business logic errors
        }
      }
    }
    
    throw lastError instanceof Error ? lastError : new Error(String(lastError));
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ==========================================
  // CONVENIENCE METHODS
  // ==========================================

  async searchWorks(searchTerm: string, options?: {
    limit?: number;
    stage?: WorkStage;
    districtId?: number;
  }): Promise<Work[]> {
    try {
      const filters: WorkFilters = {
        searchTerm,
        limit: options?.limit || 50,
        ...(options?.stage && { stage: options.stage }),
        ...(options?.districtId && { districtId: options.districtId })
      };
      
      const response = await this.getAllWorks(filters);
      return response.works;
    } catch (error) {
      throw new Error(`Failed to search works: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getWorkDetails(identifier: number | string): Promise<Work> {
    try {
      if (typeof identifier === 'number') {
        // Try by ID first, then by SNO
        try {
          return await this.getWorkById(identifier);
        } catch {
          return await this.getWorkBySno(identifier);
        }
      } else {
        throw new Error('Invalid work identifier');
      }
    } catch (error) {
      throw new Error(`Failed to fetch work details: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // ==========================================
  // VALIDATION HELPERS
  // ==========================================

  async validateWorkData(work: CreateWorkDto | UpdateWorkDto): Promise<{ valid: boolean; errors: string[] }> {
    try {
      const validationRules = await this.getValidationRules();
      const errors: string[] = [];

      // Basic validation
      if ('sno' in work && !work.sno) {
        errors.push('Serial Number is required');
      }

      if ('work_name' in work && (!work.work_name || work.work_name.trim().length < 10)) {
        errors.push('Work name must be at least 10 characters long');
      }

      if ('estimate_cost' in work && (!work.estimate_cost || work.estimate_cost <= 0)) {
        errors.push('Estimate cost must be greater than zero');
      }

      // Business rule validations
      if ('ras_amount' in work && 'estimate_cost' in work && work.ras_amount && work.estimate_cost && work.ras_amount < work.estimate_cost) {
        errors.push('RAS amount cannot be less than estimate cost');
      }

      // Check for length progress validation (only available in UpdateWorkDto)
      if ('length_completed_during_fy_23_24' in work && 'length_sanctioned_km' in work && 
          work.length_completed_during_fy_23_24 && work.length_sanctioned_km && 
          work.length_completed_during_fy_23_24 > work.length_sanctioned_km) {
        errors.push('Length completed cannot exceed sanctioned length');
      }

      return {
        valid: errors.length === 0,
        errors
      };
    } catch (error) {
      return {
        valid: false,
        errors: [`Validation failed: ${error instanceof Error ? error.message : String(error)}`]
      };
    }
  }

  // ==========================================
  // HELPER METHODS
  // ==========================================

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  }

  formatDate(date: string | Date, format: 'short' | 'long' = 'short'): string {
    if (!date) return '-';
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (format === 'long') {
      return dateObj.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    return dateObj.toLocaleDateString('en-IN');
  }

  calculateProgress(work: Work): {
    physical: number;
    financial: number;
    overall: number;
  } {
    let physical = 0;
    let financial = 0;

    // Physical progress - with null safety
    if (work.length_sanctioned_km && work.length_sanctioned_km > 0 && work.cumulative_length_completed) {
      physical = Math.round((work.cumulative_length_completed / work.length_sanctioned_km) * 100);
    }

    // Financial progress
    const totalSanctioned = work.ras_amount || work.estimate_cost;
    if (totalSanctioned > 0) {
      financial = Math.round((work.cum_exp_on_work / totalSanctioned) * 100);
    }

    // Overall progress (average of physical and financial, or financial only if no physical length)
    const overall = (work.length_sanctioned_km && work.length_sanctioned_km > 0)
      ? Math.round((physical + financial) / 2)
      : financial;

    return {
      physical: Math.min(physical, 100),
      financial: Math.min(financial, 100),
      overall: Math.min(overall, 100)
    };
  }

  getStageColor(stage: WorkStage): {
    bg: string;
    text: string;
    border: string;
  } {
    const colors = {
      [WorkStage.NOT_STARTED]: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-300'
      },
      [WorkStage.IN_PROGRESS]: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        border: 'border-blue-300'
      },
      [WorkStage.COMPLETED]: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-300'
      },
      [WorkStage.ON_HOLD]: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-300'
      },
      [WorkStage.PLANNING]: { bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-300' },

    };

    return colors[stage] || colors[WorkStage.NOT_STARTED];
  }

  isWorkDelayed(work: Work): boolean {
    if (!work.completion_date_set || work.abstract_stage_of_work === WorkStage.COMPLETED) {
      return false;
    }
    
    return new Date(work.completion_date_set) < new Date();
  }

  calculateFinancialUtilization(work: Work): number {
    const totalSanctioned = work.ras_amount || work.estimate_cost;
    return totalSanctioned > 0 ? (work.cum_exp_on_work / totalSanctioned) * 100 : 0;
  }

  calculatePhysicalProgress(work: Work): number {
    return (work.length_sanctioned_km && work.length_sanctioned_km > 0 && work.cumulative_length_completed)
      ? (work.cumulative_length_completed / work.length_sanctioned_km) * 100 
      : 0;
  }

  // ==========================================
  // BULK OPERATIONS HELPERS
  // ==========================================

  async processInBatches<T, R>(
    items: T[],
    processor: (batch: T[]) => Promise<R[]>,
    batchSize: number = 50
  ): Promise<R[]> {
    const results: R[] = [];
    
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const batchResults = await processor(batch);
      results.push(...batchResults);
    }
    
    return results;
  }

  // ==========================================
  // DOWNLOAD HELPERS
  // ==========================================

  async downloadExcelExport(filters: WorkFilters & { divisionId?: number; subDivisionId?: number } = {}): Promise<void> {
    try {
      const exportResult = await this.exportToExcel(filters);
      const worksheet = XLSX.utils.json_to_sheet(exportResult.data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Works');
      XLSX.writeFile(workbook, exportResult.filename || `works_export_${filters.divisionId || filters.subDivisionId || 'all'}.xlsx`);
    } catch (error) {
      throw new WorksApiError(
        `Failed to download Excel export: ${error instanceof Error ? error.message : String(error)}`,
        'DOWNLOAD_EXCEL_ERROR'
      );
    }
  }

  // ==========================================
  // STATISTICAL OPERATIONS
  // ==========================================

  async getWorksStatistics(): Promise<{
    totalWorks: number;
    completedWorks: number;
    inProgressWorks: number;
    delayedWorks: number;
    totalSanctioned: number;
    totalSpent: number;
    utilizationRate: number;
    avgWorkCost: number;
    avgCompletionTime: number;
  }> {
    try {
      const [allWorks, quickStats] = await Promise.all([
        this.getAllWorks(),
        this.getQuickStats()
      ]);

      const completed = allWorks.works.filter(w => w.abstract_stage_of_work === WorkStage.COMPLETED);
      const inProgress = allWorks.works.filter(w => w.abstract_stage_of_work === WorkStage.IN_PROGRESS);
      const delayed = allWorks.works.filter(w => 
        w.completion_date_set && 
        new Date(w.completion_date_set) < new Date() && 
        w.abstract_stage_of_work !== WorkStage.COMPLETED
      );

      const avgCompletionTime = completed.reduce((sum, work) => {
        if (work.agreement_date) {
          const agreementDate = new Date(work.agreement_date);
          const completionDate = new Date(); // Approximate
          const daysDiff = Math.abs(completionDate.getTime() - agreementDate.getTime()) / (1000 * 60 * 60 * 24);
          return sum + daysDiff;
        }
        return sum;
      }, 0) / (completed.length || 1); // Prevent division by zero

      return {
        totalWorks: quickStats.totalWorks,
        completedWorks: completed.length,
        inProgressWorks: inProgress.length,
        delayedWorks: delayed.length,
        totalSanctioned: quickStats.totalSanctioned,
        totalSpent: quickStats.totalSpent,
        utilizationRate: quickStats.utilizationPercentage,
        avgWorkCost: allWorks.summary.avg_estimate_cost,
        avgCompletionTime: Math.round(avgCompletionTime)
      };
    } catch (error) {
      throw new Error(`Failed to calculate statistics: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // ==========================================
  // ADDITIONAL UTILITY METHODS
  // ==========================================

  /**
   * Get works that are nearing their completion deadline
   */
  async getWorksNearingDeadline(days: number = 30): Promise<Work[]> {
    try {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + days);
      
      const response = await this.getAllWorks({
        stage: WorkStage.IN_PROGRESS
      });
      
      return response.works.filter(work => 
        work.completion_date_set && 
        new Date(work.completion_date_set) <= futureDate &&
        new Date(work.completion_date_set) >= new Date()
      );
    } catch (error) {
      throw new Error(`Failed to fetch works nearing deadline: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Get works with low financial progress
   */
  async getLowProgressWorks(threshold: number = 25): Promise<Work[]> {
    try {
      const response = await this.getAllWorks({
        stage: WorkStage.IN_PROGRESS
      });
      
      return response.works.filter(work => {
        const progress = this.calculateFinancialUtilization(work);
        return progress < threshold;
      });
    } catch (error) {
      throw new Error(`Failed to fetch low progress works: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Get summary statistics for a specific district
   */
  async getDistrictStatistics(districtId: number): Promise<{
    totalWorks: number;
    totalValue: number;
    completedWorks: number;
    delayedWorks: number;
    averageProgress: number;
  }> {
    try {
      const response = await this.getAllWorks({ districtId });
      const works = response.works;
      
      const completed = works.filter(w => w.abstract_stage_of_work === WorkStage.COMPLETED);
      const delayed = works.filter(w => this.isWorkDelayed(w));
      
      const averageProgress = works.reduce((sum, work) => {
        return sum + this.calculateFinancialUtilization(work);
      }, 0) / (works.length || 1);

      return {
        totalWorks: works.length,
        totalValue: works.reduce((sum, work) => sum + work.estimate_cost, 0),
        completedWorks: completed.length,
        delayedWorks: delayed.length,
        averageProgress: Math.round(averageProgress)
      };
    } catch (error) {
      throw new Error(`Failed to get district statistics: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Get works by contractor performance
   */
  async getWorksByContractorPerformance(): Promise<Array<{
    contractorId: number;
    contractorName: string;
    totalWorks: number;
    completedWorks: number;
    delayedWorks: number;
    performanceScore: number;
  }>> {
    try {
      const response = await this.getAllWorks();
      const works = response.works;
      
      const contractorMap = new Map<number, {
        contractorId: number;
        contractorName: string;
        works: Work[];
      }>();

      // Group works by contractor
      works.forEach(work => {
        if (work.contractor_id && work.contractor) {
          if (!contractorMap.has(work.contractor_id)) {
            contractorMap.set(work.contractor_id, {
              contractorId: work.contractor_id,
              contractorName: work.contractor.contractor_name,
              works: []
            });
          }
          contractorMap.get(work.contractor_id)!.works.push(work);
        }
      });

      // Calculate performance metrics
      return Array.from(contractorMap.values()).map(contractor => {
        const completed = contractor.works.filter(w => w.abstract_stage_of_work === WorkStage.COMPLETED);
        const delayed = contractor.works.filter(w => this.isWorkDelayed(w));
        
        const completionRate = (completed.length / contractor.works.length) * 100;
        const delayRate = (delayed.length / contractor.works.length) * 100;
        const performanceScore = Math.max(0, completionRate - delayRate);

        return {
          contractorId: contractor.contractorId,
          contractorName: contractor.contractorName,
          totalWorks: contractor.works.length,
          completedWorks: completed.length,
          delayedWorks: delayed.length,
          performanceScore: Math.round(performanceScore)
        };
      }).sort((a, b) => b.performanceScore - a.performanceScore);
    } catch (error) {
      throw new Error(`Failed to get contractor performance: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Get works summary by grant type
   */
  async getGrantTypeSummary(): Promise<Array<{
    grantLevel: GrantLevel;
    grantType: string;
    totalWorks: number;
    totalValue: number;
    totalSpent: number;
    utilizationRate: number;
  }>> {
    try {
      const response = await this.getAllWorks();
      const works = response.works;
      
      const grantMap = new Map<string, {
        grantLevel: GrantLevel;
        grantType: string;
        works: Work[];
      }>();

      // Group works by grant type
      works.forEach(work => {
        const key = `${work.oas_grant_level}-${work.grant_type}`;
        if (!grantMap.has(key)) {
          grantMap.set(key, {
            grantLevel: work.oas_grant_level,
            grantType: work.grant_type,
            works: []
          });
        }
        grantMap.get(key)!.works.push(work);
      });

      // Calculate summary metrics
      return Array.from(grantMap.values()).map(grant => {
        const totalValue = grant.works.reduce((sum, work) => sum + (work.ras_amount || work.estimate_cost), 0);
        const totalSpent = grant.works.reduce((sum, work) => sum + work.cum_exp_on_work, 0);
        const utilizationRate = totalValue > 0 ? (totalSpent / totalValue) * 100 : 0;

        return {
          grantLevel: grant.grantLevel,
          grantType: grant.grantType,
          totalWorks: grant.works.length,
          totalValue,
          totalSpent,
          utilizationRate: Math.round(utilizationRate)
        };
      }).sort((a, b) => b.totalValue - a.totalValue);
    } catch (error) {
      throw new Error(`Failed to get grant type summary: ${error instanceof Error ? error.message : String(error)}`);
    }
  }


  // In WorksApiService.ts
  async getWorksByMandal(
    mandalId: number,
    filters: {
      stage?: WorkStage;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
    } = {}
  ): Promise<{
    summary: WorkSummary;
    works: Work[];
    total: number;
    totalPages: number;
    currentPage: number;
  }> {
    try {
      if (!mandalId || isNaN(mandalId) || mandalId <= 0) {
        throw new ValidationError('Valid Mandal ID is required', 'mandalId');
      }
      const cacheKey = `works-mandal-${mandalId}-${JSON.stringify(filters)}`;
      return await this.getCached(cacheKey, () =>
        this.http.get<{
          summary: WorkSummary;
          works: Work[];
          total: number;
          totalPages: number;
          currentPage: number;
        }>(this.endpoints.worksByMandal(mandalId), filters)
      );
    } catch (error) {
      throw new WorksApiError(
        `Failed to fetch works for mandal ${mandalId}: ${error instanceof Error ? error.message : String(error)}`,
        'FETCH_WORKS_BY_MANDAL_ERROR'
      );
    }
  }



  /**
   * Get monthly expenditure trends
   */
  async getMonthlyExpenditureTrends(months: number = 12): Promise<Array<{
    month: string;
    totalSpent: number;
    worksCount: number;
    averageSpent: number;
  }>> {
    try {
      const dashboard = await this.getFinancialDashboard();
      
      return dashboard.trendAnalysis.slice(-months).map(trend => ({
        month: trend.month,
        totalSpent: trend.amount_spent,
        worksCount: trend.works_sanctioned,
        averageSpent: trend.works_sanctioned > 0 ? trend.amount_spent / trend.works_sanctioned : 0
      }));
    } catch (error) {
      throw new Error(`Failed to get monthly expenditure trends: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
}

// ==========================================
// SINGLETON INSTANCE
// ==========================================

export const worksApiService = new WorksApiService();

// ==========================================
// HOOKS FOR API SERVICE
// ==========================================

export const useWorksApi = () => {
  return {
    service: worksApiService,
    
    // Convenience methods
    formatCurrency: worksApiService.formatCurrency.bind(worksApiService),
    formatDate: worksApiService.formatDate.bind(worksApiService),
    calculateProgress: worksApiService.calculateProgress.bind(worksApiService),
    getStageColor: worksApiService.getStageColor.bind(worksApiService),
    isWorkDelayed: worksApiService.isWorkDelayed.bind(worksApiService)
  };
};

// ==========================================
// ERROR CLASSES
// ==========================================

export class WorksApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public field?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'WorksApiError';
  }
}

export class ValidationError extends WorksApiError {
  constructor(message: string, field?: string, details?: any) {
    super(message, 'VALIDATION_ERROR', field, details);
    this.name = 'ValidationError';
  }
}

export class NetworkError extends WorksApiError {
  constructor(message: string, details?: any) {
    super(message, 'NETWORK_ERROR', undefined, details);
    this.name = 'NetworkError';
  }
}

export class BusinessRuleError extends WorksApiError {
  constructor(message: string, details?: any) {
    super(message, 'BUSINESS_RULE_ERROR', undefined, details);
    this.name = 'BusinessRuleError';
  }
}





// ==========================================
// API CLIENT FACTORY
// ==========================================

export const createWorksApiService = (config?: {
  baseUrl?: string;
  timeout?: number;
  retryAttempts?: number;
  retryDelay?: number;
}): WorksApiService => {
  const mergedConfig = { ...API_CONFIG, ...config };
  return new WorksApiService(mergedConfig.baseUrl);
};

// ==========================================
// TYPE GUARDS
// ==========================================

export const isWork = (obj: any): obj is Work => {
  return obj && 
    typeof obj.work_id === 'number' &&
    typeof obj.sno === 'number' &&
    typeof obj.work_name === 'string' &&
    typeof obj.estimate_cost === 'number';
};

export const isWorkListResponse = (obj: any): obj is WorkListResponse => {
  return obj &&
    Array.isArray(obj.works) &&
    typeof obj.total === 'number' &&
    typeof obj.currentPage === 'number';
};

export const isDashboardData = (obj: any): obj is DashboardData => {
  return obj &&
    obj.overview &&
    Array.isArray(obj.trendAnalysis) &&
    Array.isArray(obj.districtComparison);
};